import React, { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import FadeIn from '../../../../components/FadeIn';
import OpenPopupEntityAppContext from './OpenPopupEntityAppContext';
import PoppedUpEntityApp from './PopupEntityApp';

const PopupEntityAppProvider = ({ children }: { children: ReactNode }) => {
  const [poppedUpApp, setPoppedUpApp] = React.useState<{
    app: EntityDetails.App;
    entity: EntityDetails.Entity;
    entityValue: string;
    onEntityAppClosed?: () => void;
    previousTabId?: string | null;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const closingAppRef = React.useRef<{
    previousTabId: string | null | undefined;
  } | null>(null);

  const openEntityPopup = useCallback(
    ({
      app,
      entity,
      entityValue,
      initialTabId,
      overrideMode,
      selectedBonusPeriodId,
      onEntityAppClosed,
    }: {
      app: EntityDetails.App;
      entity: EntityDetails.Entity;
      entityValue: string;
      initialTabId?: string;
      overrideMode?: boolean;
      selectedBonusPeriodId?: string;
      onEntityAppClosed?: () => void;
    }) => {
      const previousTabId = searchParams.get('tabId');
      if (initialTabId || overrideMode !== undefined || selectedBonusPeriodId) {
        setSearchParams((currentParams) => {
          if (initialTabId) {
            currentParams.set('tabId', initialTabId);
          }
          if (overrideMode !== undefined) {
            currentParams.set('overrideMode', overrideMode ? 'true' : 'false');
          }
          if (selectedBonusPeriodId) {
            currentParams.set('selectedBonusPeriodId', selectedBonusPeriodId);
          }
          return currentParams;
        });
      }
      setPoppedUpApp({
        app,
        entity,
        entityValue,
        onEntityAppClosed,
        previousTabId,
      });
    },
    [searchParams, setSearchParams],
  );

  const closeEntityPopup = useCallback(() => {
    if (poppedUpApp?.onEntityAppClosed) {
      poppedUpApp.onEntityAppClosed();
    }
    closingAppRef.current = { previousTabId: poppedUpApp?.previousTabId };
    setPoppedUpApp(undefined);
  }, [poppedUpApp]);

  useEffect(() => {
    if (!poppedUpApp && closingAppRef.current !== null) {
      const { previousTabId } = closingAppRef.current;
      closingAppRef.current = null;
      setSearchParams((currentParams) => {
        if (previousTabId !== undefined) {
          if (previousTabId) {
            currentParams.set('tabId', previousTabId);
          } else {
            currentParams.delete('tabId');
          }
        }
        currentParams.delete('overrideMode');
        currentParams.delete('selectedBonusPeriodId');
        return currentParams;
      });
    }
  }, [poppedUpApp, setSearchParams]);

  const contextValue = useMemo(
    () => ({ openEntityPopup, isEntityPopupOpen: !!poppedUpApp }),
    [openEntityPopup, poppedUpApp],
  );

  return (
    <OpenPopupEntityAppContext.Provider value={contextValue}>
      {children}
      <FadeIn isOpen={!!poppedUpApp}>
        {!!poppedUpApp && (
          <PoppedUpEntityApp
            poppedUpApp={poppedUpApp}
            closeEntityPopup={closeEntityPopup}
          />
        )}
      </FadeIn>
    </OpenPopupEntityAppContext.Provider>
  );
};

export default PopupEntityAppProvider;
